import React, { useState, useEffect, useRef } from 'react';
import { 
  Building, Search, ShieldCheck, Briefcase, Wheat, Droplet, 
  Cookie, CupSoda, Container, Beef, MapPin, Award, Truck, Target
} from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { mockData } from '../../mock';

const iconMap = {
  building: Building,
  search: Search,
  'shield-check': ShieldCheck,
  briefcase: Briefcase,
  wheat: Wheat,
  droplet: Droplet,
  cheese: Cookie,
  'cup-soda': CupSoda,
  container: Container,
  beef: Beef,
  'map-pin': MapPin,
  award: Award,
  truck: Truck,
  target: Target
};

const ServicesSection = () => {
  const [servicesAnimated, setServicesAnimated] = useState(false);
  const servicesRef = useRef(null);

  useEffect(() => {
    const servicesObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !servicesAnimated) {
          setServicesAnimated(true);
        }
      },
      { threshold: 0.2 }
    );

    if (servicesRef.current) {
      servicesObserver.observe(servicesRef.current);
    }

    return () => {
      if (servicesRef.current) {
        servicesObserver.unobserve(servicesRef.current);
      }
    };
  }, [servicesAnimated]);

  return (
    <section 
      id="services" 
      className="py-20 px-6 bg-gradient-to-br from-amber-50 via-stone-50 to-orange-50"
      ref={servicesRef}
    >
      <div className={`container mx-auto max-w-7xl ${servicesAnimated ? 'services-animate-active' : 'services-animate-ready'}`}>
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-blue-900 font-semibold tracking-wide uppercase text-sm services-label">
            Our Services
          </span>
          <h2 className="md:text-5xl !font-bold !text-4xl mt-3 mb-6 text-stone-900">
            <span className="services-headline">
              Food import and <span className="text-blue-900">export services</span>
            </span>
          </h2>
          <p className="text-lg text-stone-600 services-subtext">
            From sourcing to delivery, we provide comprehensive services tailored to your business needs.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          {mockData.services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            return (
              <Card
                key={service.id}
                className={`border-stone-200 hover:border-blue-900 transition-all duration-300 hover:shadow-xl group services-card services-card-${index}`}
              >
                <CardContent className="p-8">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-900 transition-colors duration-300">
                        <IconComponent className="text-blue-900 group-hover:text-white transition-colors duration-300" size={28} />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-stone-900 mb-3">
                        {service.title}
                      </h3>
                      <p className="text-stone-600 leading-relaxed">
                        {service.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
